/**
 * Terms of Service content, one entry per supported site language.
 *
 * Kept out of the i18n JSON files on purpose: these are long legal paragraphs
 * with their own block structure (lists, a highlighted clause), not interface
 * strings, and mixing them into the translation catalogues makes those files
 * unusable for everyday UI work.
 *
 * Block shapes inside `body`:
 *   'text'                 → a paragraph (may contain inline <strong> / <a>)
 *   { list: [...] }        → a bulleted list
 *   { highlight: [...] }   → paragraphs inside the emphasised callout box
 */

export const EFFECTIVE_DATE = {
  bg: '1 януари 2024 г.',
  en: '1 January 2024',
  el: '1 Ιανουαρίου 2024',
  ar: '1 يناير 2024',
  he: '1 בינואר 2024',
};

export const HERO = {
  bg: {
    eyebrow: 'Правна информация',
    title: 'Общи условия',
    lead: 'Условия за ползване на Invoicescan — услуга за автоматично разчитане и извличане на данни от фактури и други счетоводни документи.',
    effective: (date) => `В сила от ${date}`,
  },
  en: {
    eyebrow: 'Legal',
    title: 'Terms of Service',
    lead: 'Terms governing the use of Invoicescan — a service that reads invoices and other accounting documents and extracts their data automatically.',
    effective: (date) => `Effective ${date}`,
  },
  el: {
    eyebrow: 'Νομικές πληροφορίες',
    title: 'Όροι Χρήσης',
    lead: 'Όροι που διέπουν τη χρήση του Invoicescan — μιας υπηρεσίας που διαβάζει τιμολόγια και άλλα λογιστικά παραστατικά και εξάγει αυτόματα τα δεδομένα τους.',
    effective: (date) => `Σε ισχύ από ${date}`,
  },
  ar: {
    eyebrow: 'معلومات قانونية',
    title: 'شروط الخدمة',
    lead: 'الشروط التي تحكم استخدام Invoicescan — خدمة تقرأ الفواتير وغيرها من المستندات المحاسبية وتستخرج بياناتها تلقائيًا.',
    effective: (date) => `سارية اعتبارًا من ${date}`,
  },
  he: {
    eyebrow: 'מידע משפטי',
    title: 'תנאי שימוש',
    lead: 'התנאים החלים על השימוש ב‑Invoicescan — שירות הקורא חשבוניות ומסמכים חשבונאיים אחרים ומחלץ מהם נתונים באופן אוטומטי.',
    effective: (date) => `בתוקף מ‑${date}`,
  },
};

export const SECTIONS = {
  // ------------------------------------------------------------------ BG ----
  bg: [
    {
      title: 'Обхват и приемане',
      body: [
        'Настоящите Общи условия уреждат отношенията между Invoicescan („Доставчик“, „ние“) и всяко лице, което използва платформата Invoicescan, уеб приложението и свързаните с тях приложно-програмни интерфейси („Услугата“).',
        'Създавайки акаунт или използвайки Услугата, Вие потвърждавате, че сте прочели и приемате тези условия. Ако не сте съгласни с тях, не използвайте Услугата.',
        'Ако използвате Услугата от името на юридическо лице, Вие декларирате, че имате представителна власт да обвържете това лице с настоящите условия.',
      ],
    },
    {
      title: 'Описание на Услугата',
      body: [
        'Invoicescan приема качени от Вас документи — изображения и PDF файлове на фактури, касови бележки, кредитни и дебитни известия — и извлича от тях структурирани данни: контрагенти, номера и дати, редови позиции, количества, данъчни основи, ставки и суми.',
        'Извлечените данни могат да бъдат преглеждани и редактирани в приложението и да бъдат експортирани в поддържаните счетоводни формати.',
        'Услугата се предоставя на принципа „както е“ и представлява помощно средство за въвеждане на данни. Тя не е счетоводна, данъчна или правна консултация и не замества проверката от страна на квалифицирано лице.',
      ],
    },
    {
      title: 'Регистрация и акаунт',
      body: [
        'За ползване на Услугата е необходима регистрация. Вие се задължавате да предоставите вярна и актуална информация и да я поддържате такава.',
        {
          list: [
            'Отговаряте за опазването на паролата и на достъпа до акаунта си.',
            'Отговаряте за всички действия, извършени чрез Вашия акаунт.',
            'Уведомявате ни незабавно при съмнение за неоторизиран достъп.',
            'Не предоставяте достъпа си на трети лица извън Вашата организация.',
          ],
        },
        'Имаме право да спрем или прекратим акаунт при съществено нарушение на настоящите условия.',
      ],
    },
    {
      title: 'Абонамент, цени и плащане',
      body: [
        'Услугата се предоставя по абонаментни планове с определен брой обработени документи за отчетен период. Актуалните планове и цени са публикувани на страницата с цените.',
        {
          list: [
            'Абонаментът се подновява автоматично за следващия период, освен ако не бъде прекратен преди края на текущия.',
            'Цените са в евро и не включват дължимите данъци, освен ако не е указано друго.',
            'Неизползваните за периода документи не се прехвърлят към следващ период, освен ако изрично не е договорено.',
            'При неплащане достъпът до Услугата може да бъде ограничен до уреждане на задължението.',
          ],
        },
        'Запазваме правото да променяме цените, като Ви уведомяваме поне 30 дни предварително. Промяната влиза в сила от следващия абонаментен период.',
      ],
    },
    {
      title: 'Допустимо ползване',
      body: [
        'При ползване на Услугата Вие се задължавате да не:',
        {
          list: [
            'качвате документи, за които нямате право да обработвате;',
            'използвате Услугата за незаконна дейност, включително за обработка на подправени документи;',
            'се опитвате да заобиколите ограниченията на плана си или мерките за сигурност;',
            'натоварвате умишлено инфраструктурата с автоматизирани заявки извън документираните лимити;',
            'извличате, копирате или препродавате Услугата или части от нея.',
          ],
        },
      ],
    },
    {
      title: 'Вашите документи и данни',
      body: [
        'Качените от Вас документи и извлечените от тях данни остават Ваша собственост. Ние не придобиваме права върху съдържанието им.',
        'Обработваме документите единствено с цел предоставяне на Услугата — тяхното разчитане, съхранение до момента на изтриване и експорт по Ваше нареждане. Не използваме съдържанието на Вашите документи за обучение на модели и не го предоставяме на трети лица извън техническите доставчици, необходими за функционирането на Услугата.',
        'Обработката на лични данни, съдържащи се в документите, е подробно описана в <a href="/privacy-policy.html">Политиката за поверителност</a>.',
      ],
    },
    {
      title: 'Изтриване на документи и срок на съхранение',
      body: [
        {
          highlight: [
            '<strong>Не съхраняваме фактурите Ви след изтриването им от системата.</strong> Когато изтриете документ, файлът и всички извлечени от него данни се заличават — файлът се премахва от хранилището, а записът с извлеченото съдържание се изтрива окончателно от базата данни.',
            'Изтриването е <strong>необратимо</strong>. Няма „кошче“, от което документът да бъде възстановен, и не пазим негово копие за собствени нужди. Ако документът Ви е нужен, изтеглете или експортирайте данните преди да го изтриете.',
          ],
        },
        'След изтриване запазваме единствено технически запис за самото събитие — идентификатор на записа, потребител, действие и дата и час. Този запис не съдържа изображението на документа, нито извлечените от него данни, и служи за отчитане на потреблението по абонаментния Ви план и за защита от злоупотреби.',
        'До момента на изтриване документите се съхраняват, за да са достъпни в акаунта Ви. При прекратяване на акаунта данните, свързани с него, се заличават.',
        'Вие сами преценявате кога да изтриете документ. Обръщаме внимание, че счетоводното и данъчното законодателство може да Ви задължава да съхранявате оригиналните документи за определен срок — това задължение остава изцяло Ваше и не се поема от Услугата.',
      ],
    },
    {
      title: 'Точност на извличането',
      body: [
        'Извличането на данни се извършва автоматизирано. Въпреки полаганите усилия за високо качество, разчитането може да съдържа грешки — особено при слабо качество на сканиране, ръкописен текст, нестандартни формати или повредени документи.',
        '<strong>Вие отговаряте за проверката на извлечените данни</strong> преди използването им за счетоводни, данъчни или други цели. Приложението сигнализира част от несъответствията, но тези проверки са помощни и не гарантират пълнота.',
      ],
    },
    {
      title: 'Достъпност и поддръжка',
      body: [
        'Полагаме разумни усилия Услугата да бъде достъпна непрекъснато, но не гарантираме работа без прекъсвания. Възможни са планови прекъсвания за поддръжка, за които се стремим да уведомяваме предварително.',
        'Услугата зависи от външни доставчици на изчислителни ресурси. Прекъсване при такъв доставчик може да се отрази на достъпността.',
      ],
    },
    {
      title: 'Интелектуална собственост',
      body: [
        'Платформата, изходният код, интерфейсът, документацията и марката Invoicescan са наша собственост или са ни предоставени с надлежни права. Настоящите условия Ви предоставят ограничено, непрехвърлимо и неизключително право да ползвате Услугата за срока на абонамента.',
      ],
    },
    {
      title: 'Ограничение на отговорността',
      body: [
        'В максималната степен, допустима от приложимото право, не носим отговорност за пропуснати ползи, загуба на данни, вреди от неточно извлечени данни или косвени вреди, произтичащи от ползването или невъзможността за ползване на Услугата.',
        'Общата ни отговорност към Вас за период от дванадесет месеца не надвишава сумата, която сте заплатили за Услугата през същия период.',
        'Тези ограничения не изключват отговорност, която не може да бъде изключена по силата на императивни разпоредби на закона.',
      ],
    },
    {
      title: 'Прекратяване',
      body: [
        'Можете да прекратите ползването на Услугата по всяко време чрез прекратяване на абонамента или изтриване на акаунта.',
        'Имаме право да прекратим или ограничим достъпа при съществено нарушение на настоящите условия, при неплащане или при действия, застрашаващи сигурността на Услугата и на останалите потребители.',
        'Преди прекратяване експортирайте данните, които са Ви нужни — след заличаването им възстановяване не е възможно.',
      ],
    },
    {
      title: 'Промени в условията',
      body: [
        'Можем да изменяме настоящите условия. При съществени промени ще Ви уведомим по имейл или чрез съобщение в приложението поне 30 дни преди влизането им в сила. Продължаването на ползването след тази дата означава приемане на изменените условия.',
      ],
    },
    {
      title: 'Приложимо право и спорове',
      body: [
        'За неуредените въпроси се прилага българското законодателство. Страните полагат усилия споровете да бъдат решени по споразумение, а при невъзможност — от компетентния български съд.',
        'Ако сте потребител по смисъла на Закона за защита на потребителите, законовите Ви права остават незасегнати.',
      ],
    },
    {
      title: 'Контакти',
      body: [
        'Въпроси по настоящите условия изпращайте на <a href="mailto:invoicescan.eu@gmail.com">invoicescan.eu@gmail.com</a>.',
      ],
    },
  ],

  // ------------------------------------------------------------------ EN ----
  en: [
    {
      title: 'Scope and acceptance',
      body: [
        'These Terms govern the relationship between Invoicescan (the “Provider”, “we”) and anyone using the Invoicescan platform, web application and related application programming interfaces (the “Service”).',
        'By creating an account or using the Service you confirm that you have read and accept these Terms. If you do not agree with them, do not use the Service.',
        'If you use the Service on behalf of a legal entity, you represent that you are authorised to bind that entity to these Terms.',
      ],
    },
    {
      title: 'What the Service does',
      body: [
        'Invoicescan takes documents you upload — images and PDFs of invoices, receipts, credit and debit notes — and extracts structured data from them: counterparties, numbers and dates, line items, quantities, tax bases, rates and amounts.',
        'The extracted data can be reviewed and corrected in the application and exported to the supported accounting formats.',
        'The Service is provided “as is” and is a data-entry aid. It is not accounting, tax or legal advice and does not replace review by a qualified person.',
      ],
    },
    {
      title: 'Registration and account',
      body: [
        'Using the Service requires registration. You undertake to provide accurate, current information and to keep it up to date.',
        {
          list: [
            'You are responsible for safeguarding your password and account access.',
            'You are responsible for all activity carried out through your account.',
            'You will notify us promptly of any suspected unauthorised access.',
            'You will not share your access with third parties outside your organisation.',
          ],
        },
        'We may suspend or terminate an account in the event of a material breach of these Terms.',
      ],
    },
    {
      title: 'Subscription, pricing and payment',
      body: [
        'The Service is offered on subscription plans with a set number of processed documents per billing period. Current plans and prices are published on the pricing page.',
        {
          list: [
            'Subscriptions renew automatically unless cancelled before the end of the current period.',
            'Prices are in euro and exclude applicable taxes unless stated otherwise.',
            'Unused documents do not roll over to the next period unless expressly agreed.',
            'Access may be limited on non-payment until the outstanding amount is settled.',
          ],
        },
        'We may change prices with at least 30 days’ notice. Changes take effect from the next billing period.',
      ],
    },
    {
      title: 'Acceptable use',
      body: [
        'When using the Service you agree not to:',
        {
          list: [
            'upload documents you are not entitled to process;',
            'use the Service for unlawful activity, including processing forged documents;',
            'attempt to circumvent your plan’s limits or any security measures;',
            'deliberately overload the infrastructure with automated requests beyond the documented limits;',
            'scrape, copy or resell the Service or any part of it.',
          ],
        },
      ],
    },
    {
      title: 'Your documents and data',
      body: [
        'The documents you upload and the data extracted from them remain yours. We acquire no rights over their content.',
        'We process documents solely to provide the Service — reading them, storing them until you delete them, and exporting on your instruction. We do not use the content of your documents to train models and do not disclose it to third parties beyond the technical providers required to operate the Service.',
        'The processing of personal data contained in documents is described in the <a href="/privacy-policy.html">Privacy Policy</a>.',
      ],
    },
    {
      title: 'Deletion and retention',
      body: [
        {
          highlight: [
            '<strong>We do not keep your invoices once you delete them from the system.</strong> When you delete a document, the file and all data extracted from it are erased — the file is removed from storage and the record holding the extracted content is permanently deleted from the database.',
            'Deletion is <strong>irreversible</strong>. There is no bin to restore from, and we keep no copy for our own purposes. If you need a document, download or export its data before deleting it.',
          ],
        },
        'After deletion we retain only a technical record of the event itself — the record identifier, the user, the action and a timestamp. That record contains neither the document image nor any extracted data, and exists to meter usage against your plan and to guard against abuse.',
        'Until you delete them, documents are stored so they remain available in your account. When an account is terminated, the data associated with it is erased.',
        'Deciding when to delete a document is yours alone. Note that accounting and tax law may require you to retain original documents for a set period; that obligation remains entirely yours and is not assumed by the Service.',
      ],
    },
    {
      title: 'Extraction accuracy',
      body: [
        'Data extraction is automated. Despite our efforts, results may contain errors — particularly with poor scan quality, handwriting, unusual layouts or damaged documents.',
        '<strong>You are responsible for verifying the extracted data</strong> before relying on it for accounting, tax or any other purpose. The application flags some inconsistencies, but those checks are an aid and guarantee nothing.',
      ],
    },
    {
      title: 'Availability and support',
      body: [
        'We make reasonable efforts to keep the Service available, but do not guarantee uninterrupted operation. Planned maintenance windows may occur, and we aim to announce them in advance.',
        'The Service depends on third-party compute providers. An outage at such a provider may affect availability.',
      ],
    },
    {
      title: 'Intellectual property',
      body: [
        'The platform, its source code, interface, documentation and the Invoicescan brand are ours or duly licensed to us. These Terms grant you a limited, non-transferable, non-exclusive right to use the Service for the duration of your subscription.',
      ],
    },
    {
      title: 'Limitation of liability',
      body: [
        'To the maximum extent permitted by applicable law, we are not liable for lost profits, loss of data, damages arising from inaccurately extracted data, or indirect damages resulting from the use of or inability to use the Service.',
        'Our total liability to you over any twelve-month period will not exceed the amount you paid for the Service in that period.',
        'These limitations do not exclude liability that cannot be excluded under mandatory provisions of law.',
      ],
    },
    {
      title: 'Termination',
      body: [
        'You may stop using the Service at any time by cancelling your subscription or deleting your account.',
        'We may terminate or restrict access in the event of a material breach of these Terms, on non-payment, or for conduct that threatens the security of the Service or of other users.',
        'Export whatever data you need before termination — once erased it cannot be recovered.',
      ],
    },
    {
      title: 'Changes to these Terms',
      body: [
        'We may amend these Terms. For material changes we will notify you by email or in-app at least 30 days before they take effect. Continuing to use the Service after that date constitutes acceptance of the amended Terms.',
      ],
    },
    {
      title: 'Governing law and disputes',
      body: [
        'Matters not covered here are governed by Bulgarian law. The parties will seek to resolve disputes amicably and, failing that, before the competent Bulgarian court.',
        'If you are a consumer, your statutory rights remain unaffected.',
      ],
    },
    {
      title: 'Contact',
      body: [
        'Questions about these Terms go to <a href="mailto:invoicescan.eu@gmail.com">invoicescan.eu@gmail.com</a>.',
      ],
    },
  ],

  // ------------------------------------------------------------------ EL ----
  el: [
    {
      title: 'Πεδίο εφαρμογής και αποδοχή',
      body: [
        'Οι παρόντες Όροι διέπουν τη σχέση μεταξύ της Invoicescan («Πάροχος», «εμείς») και κάθε προσώπου που χρησιμοποιεί την πλατφόρμα Invoicescan, την εφαρμογή ιστού και τις σχετικές διεπαφές προγραμματισμού εφαρμογών (η «Υπηρεσία»).',
        'Δημιουργώντας λογαριασμό ή χρησιμοποιώντας την Υπηρεσία, επιβεβαιώνετε ότι έχετε διαβάσει και αποδέχεστε τους παρόντες Όρους. Εάν δεν συμφωνείτε, μην χρησιμοποιείτε την Υπηρεσία.',
        'Εάν χρησιμοποιείτε την Υπηρεσία για λογαριασμό νομικού προσώπου, δηλώνετε ότι έχετε την εξουσία να δεσμεύσετε το πρόσωπο αυτό με τους παρόντες Όρους.',
      ],
    },
    {
      title: 'Περιγραφή της Υπηρεσίας',
      body: [
        'Η Invoicescan λαμβάνει τα παραστατικά που ανεβάζετε — εικόνες και αρχεία PDF τιμολογίων, αποδείξεων, πιστωτικών και χρεωστικών σημειωμάτων — και εξάγει από αυτά δομημένα δεδομένα: αντισυμβαλλόμενους, αριθμούς και ημερομηνίες, γραμμές ειδών, ποσότητες, φορολογητέες αξίες, συντελεστές και ποσά.',
        'Τα εξαγόμενα δεδομένα μπορούν να ελεγχθούν και να διορθωθούν μέσα στην εφαρμογή και να εξαχθούν στις υποστηριζόμενες λογιστικές μορφές.',
        'Η Υπηρεσία παρέχεται «ως έχει» και αποτελεί βοηθητικό εργαλείο καταχώρισης δεδομένων. Δεν συνιστά λογιστική, φορολογική ή νομική συμβουλή και δεν υποκαθιστά τον έλεγχο από αρμόδιο πρόσωπο.',
      ],
    },
    {
      title: 'Εγγραφή και λογαριασμός',
      body: [
        'Η χρήση της Υπηρεσίας προϋποθέτει εγγραφή. Αναλαμβάνετε να παρέχετε ακριβή και επίκαιρα στοιχεία και να τα διατηρείτε ενημερωμένα.',
        {
          list: [
            'Είστε υπεύθυνοι για τη φύλαξη του κωδικού και της πρόσβασης στον λογαριασμό σας.',
            'Είστε υπεύθυνοι για κάθε ενέργεια που πραγματοποιείται μέσω του λογαριασμού σας.',
            'Μας ειδοποιείτε άμεσα σε περίπτωση υποψίας μη εξουσιοδοτημένης πρόσβασης.',
            'Δεν παραχωρείτε την πρόσβασή σας σε τρίτους εκτός του οργανισμού σας.',
          ],
        },
        'Διατηρούμε το δικαίωμα να αναστείλουμε ή να τερματίσουμε λογαριασμό σε περίπτωση ουσιώδους παράβασης των παρόντων Όρων.',
      ],
    },
    {
      title: 'Συνδρομή, τιμές και πληρωμή',
      body: [
        'Η Υπηρεσία προσφέρεται με συνδρομητικά πακέτα που περιλαμβάνουν συγκεκριμένο αριθμό επεξεργασμένων παραστατικών ανά περίοδο χρέωσης. Τα ισχύοντα πακέτα και οι τιμές δημοσιεύονται στη σελίδα τιμολόγησης.',
        {
          list: [
            'Η συνδρομή ανανεώνεται αυτόματα, εκτός εάν ακυρωθεί πριν από τη λήξη της τρέχουσας περιόδου.',
            'Οι τιμές είναι σε ευρώ και δεν περιλαμβάνουν τους αναλογούντες φόρους, εκτός εάν ορίζεται διαφορετικά.',
            'Τα αχρησιμοποίητα παραστατικά δεν μεταφέρονται στην επόμενη περίοδο, εκτός εάν συμφωνηθεί ρητά.',
            'Σε περίπτωση μη πληρωμής, η πρόσβαση μπορεί να περιοριστεί έως την εξόφληση της οφειλής.',
          ],
        },
        'Διατηρούμε το δικαίωμα αλλαγής των τιμών με προειδοποίηση τουλάχιστον 30 ημερών. Οι αλλαγές ισχύουν από την επόμενη περίοδο χρέωσης.',
      ],
    },
    {
      title: 'Αποδεκτή χρήση',
      body: [
        'Κατά τη χρήση της Υπηρεσίας δεσμεύεστε να μην:',
        {
          list: [
            'ανεβάζετε παραστατικά που δεν δικαιούστε να επεξεργαστείτε·',
            'χρησιμοποιείτε την Υπηρεσία για παράνομη δραστηριότητα, περιλαμβανομένης της επεξεργασίας πλαστών παραστατικών·',
            'επιχειρείτε να παρακάμψετε τα όρια του πακέτου σας ή τα μέτρα ασφαλείας·',
            'επιβαρύνετε σκόπιμα την υποδομή με αυτοματοποιημένα αιτήματα πέραν των τεκμηριωμένων ορίων·',
            'αντλείτε, αντιγράφετε ή μεταπωλείτε την Υπηρεσία ή μέρος αυτής.',
          ],
        },
      ],
    },
    {
      title: 'Τα παραστατικά και τα δεδομένα σας',
      body: [
        'Τα παραστατικά που ανεβάζετε και τα δεδομένα που εξάγονται από αυτά παραμένουν δικά σας. Δεν αποκτούμε δικαιώματα επί του περιεχομένου τους.',
        'Επεξεργαζόμαστε τα παραστατικά αποκλειστικά για την παροχή της Υπηρεσίας — για την ανάγνωσή τους, την αποθήκευσή τους έως τη διαγραφή τους και την εξαγωγή κατ’ εντολή σας. Δεν χρησιμοποιούμε το περιεχόμενο των παραστατικών σας για την εκπαίδευση μοντέλων και δεν το γνωστοποιούμε σε τρίτους πέραν των τεχνικών παρόχων που είναι απαραίτητοι για τη λειτουργία της Υπηρεσίας.',
        'Η επεξεργασία των προσωπικών δεδομένων που περιέχονται στα παραστατικά περιγράφεται στην <a href="/privacy-policy.html">Πολιτική Απορρήτου</a>.',
      ],
    },
    {
      title: 'Διαγραφή και διατήρηση',
      body: [
        {
          highlight: [
            '<strong>Δεν διατηρούμε τα τιμολόγιά σας μετά τη διαγραφή τους από το σύστημα.</strong> Όταν διαγράφετε ένα παραστατικό, το αρχείο και όλα τα δεδομένα που εξήχθησαν από αυτό διαγράφονται — το αρχείο αφαιρείται από τον αποθηκευτικό χώρο και η εγγραφή με το εξαγόμενο περιεχόμενο διαγράφεται οριστικά από τη βάση δεδομένων.',
            'Η διαγραφή είναι <strong>μη αναστρέψιμη</strong>. Δεν υπάρχει κάδος ανάκτησης και δεν κρατάμε αντίγραφο για δικούς μας σκοπούς. Εάν χρειάζεστε ένα παραστατικό, κατεβάστε ή εξαγάγετε τα δεδομένα του πριν το διαγράψετε.',
          ],
        },
        'Μετά τη διαγραφή διατηρούμε μόνο μια τεχνική εγγραφή του ίδιου του συμβάντος — αναγνωριστικό εγγραφής, χρήστη, ενέργεια και χρονική σήμανση. Η εγγραφή αυτή δεν περιέχει ούτε την εικόνα του παραστατικού ούτε τα εξαγόμενα δεδομένα και εξυπηρετεί τη μέτρηση της χρήσης βάσει του πακέτου σας και την προστασία από καταχρήσεις.',
        'Έως τη διαγραφή τους, τα παραστατικά αποθηκεύονται ώστε να παραμένουν διαθέσιμα στον λογαριασμό σας. Με τον τερματισμό του λογαριασμού, τα σχετικά δεδομένα διαγράφονται.',
        'Η απόφαση για το πότε θα διαγράψετε ένα παραστατικό είναι αποκλειστικά δική σας. Επισημαίνουμε ότι η λογιστική και φορολογική νομοθεσία ενδέχεται να σας υποχρεώνει να διατηρείτε τα πρωτότυπα παραστατικά για ορισμένο χρονικό διάστημα· η υποχρέωση αυτή παραμένει εξ ολοκλήρου δική σας και δεν αναλαμβάνεται από την Υπηρεσία.',
      ],
    },
    {
      title: 'Ακρίβεια της εξαγωγής',
      body: [
        'Η εξαγωγή δεδομένων γίνεται αυτοματοποιημένα. Παρά τις προσπάθειές μας, τα αποτελέσματα ενδέχεται να περιέχουν σφάλματα — ιδίως σε περιπτώσεις κακής ποιότητας σάρωσης, χειρόγραφου κειμένου, ασυνήθιστων διατάξεων ή φθαρμένων παραστατικών.',
        '<strong>Είστε υπεύθυνοι για τον έλεγχο των εξαγόμενων δεδομένων</strong> πριν τα χρησιμοποιήσετε για λογιστικούς, φορολογικούς ή άλλους σκοπούς. Η εφαρμογή επισημαίνει ορισμένες αποκλίσεις, αλλά οι έλεγχοι αυτοί είναι βοηθητικοί και δεν εγγυώνται πληρότητα.',
      ],
    },
    {
      title: 'Διαθεσιμότητα και υποστήριξη',
      body: [
        'Καταβάλλουμε εύλογες προσπάθειες ώστε η Υπηρεσία να είναι διαθέσιμη, χωρίς ωστόσο να εγγυόμαστε αδιάλειπτη λειτουργία. Ενδέχεται να υπάρξουν προγραμματισμένες διακοπές συντήρησης, τις οποίες επιδιώκουμε να ανακοινώνουμε εκ των προτέρων.',
        'Η Υπηρεσία εξαρτάται από τρίτους παρόχους υπολογιστικών πόρων. Διακοπή σε τέτοιον πάροχο ενδέχεται να επηρεάσει τη διαθεσιμότητα.',
      ],
    },
    {
      title: 'Πνευματική ιδιοκτησία',
      body: [
        'Η πλατφόρμα, ο πηγαίος κώδικας, η διεπαφή, η τεκμηρίωση και το σήμα Invoicescan μάς ανήκουν ή μας έχουν παραχωρηθεί νομίμως. Οι παρόντες Όροι σάς παρέχουν περιορισμένο, μη μεταβιβάσιμο και μη αποκλειστικό δικαίωμα χρήσης της Υπηρεσίας για τη διάρκεια της συνδρομής σας.',
      ],
    },
    {
      title: 'Περιορισμός ευθύνης',
      body: [
        'Στον μέγιστο βαθμό που επιτρέπει το εφαρμοστέο δίκαιο, δεν ευθυνόμαστε για διαφυγόντα κέρδη, απώλεια δεδομένων, ζημίες από ανακριβώς εξαγόμενα δεδομένα ή έμμεσες ζημίες που απορρέουν από τη χρήση ή την αδυναμία χρήσης της Υπηρεσίας.',
        'Η συνολική μας ευθύνη έναντι υμών για οποιαδήποτε περίοδο δώδεκα μηνών δεν υπερβαίνει το ποσό που καταβάλατε για την Υπηρεσία κατά την ίδια περίοδο.',
        'Οι περιορισμοί αυτοί δεν αποκλείουν ευθύνη που δεν επιτρέπεται να αποκλειστεί βάσει αναγκαστικών διατάξεων του νόμου.',
      ],
    },
    {
      title: 'Λύση της σύμβασης',
      body: [
        'Μπορείτε να διακόψετε τη χρήση της Υπηρεσίας ανά πάσα στιγμή, ακυρώνοντας τη συνδρομή σας ή διαγράφοντας τον λογαριασμό σας.',
        'Διατηρούμε το δικαίωμα να τερματίσουμε ή να περιορίσουμε την πρόσβαση σε περίπτωση ουσιώδους παράβασης των παρόντων Όρων, μη πληρωμής ή συμπεριφοράς που θέτει σε κίνδυνο την ασφάλεια της Υπηρεσίας και των λοιπών χρηστών.',
        'Πριν από τον τερματισμό, εξαγάγετε όσα δεδομένα χρειάζεστε — μετά τη διαγραφή τους η ανάκτηση δεν είναι δυνατή.',
      ],
    },
    {
      title: 'Τροποποιήσεις των Όρων',
      body: [
        'Ενδέχεται να τροποποιήσουμε τους παρόντες Όρους. Για ουσιώδεις αλλαγές θα σας ειδοποιήσουμε μέσω email ή εντός της εφαρμογής τουλάχιστον 30 ημέρες πριν από την έναρξη ισχύος τους. Η συνέχιση της χρήσης μετά την ημερομηνία αυτή συνιστά αποδοχή των τροποποιημένων Όρων.',
      ],
    },
    {
      title: 'Εφαρμοστέο δίκαιο και διαφορές',
      body: [
        'Για ό,τι δεν ρυθμίζεται εδώ εφαρμόζεται το βουλγαρικό δίκαιο. Τα μέρη επιδιώκουν τη φιλική επίλυση των διαφορών και, εφόσον αυτό δεν καταστεί δυνατό, αρμόδια είναι τα βουλγαρικά δικαστήρια.',
        'Εάν είστε καταναλωτής, τα εκ του νόμου δικαιώματά σας παραμένουν ανεπηρέαστα.',
      ],
    },
    {
      title: 'Επικοινωνία',
      body: [
        'Για ερωτήσεις σχετικά με τους παρόντες Όρους επικοινωνήστε στο <a href="mailto:invoicescan.eu@gmail.com">invoicescan.eu@gmail.com</a>.',
      ],
    },
  ],

  // ------------------------------------------------------------------ AR ----
  ar: [
    {
      title: 'النطاق والقبول',
      body: [
        'تحكم هذه الشروط العلاقة بين Invoicescan («المزوّد»، «نحن») وكل شخص يستخدم منصة Invoicescan وتطبيق الويب وواجهات برمجة التطبيقات المرتبطة بها («الخدمة»).',
        'بإنشاء حساب أو باستخدام الخدمة، فإنك تؤكد أنك قرأت هذه الشروط وقبلتها. وإذا كنت لا توافق عليها، فلا تستخدم الخدمة.',
        'إذا كنت تستخدم الخدمة نيابةً عن شخص اعتباري، فإنك تُقر بأنك مخوّل بإلزام ذلك الشخص بهذه الشروط.',
      ],
    },
    {
      title: 'وصف الخدمة',
      body: [
        'تستقبل Invoicescan المستندات التي ترفعها — صور وملفات PDF للفواتير والإيصالات وإشعارات الدائن والمدين — وتستخرج منها بيانات منظمة: الأطراف المقابلة، والأرقام والتواريخ، وبنود السطور، والكميات، والأوعية الضريبية، والنسب، والمبالغ.',
        'يمكن مراجعة البيانات المستخرجة وتصحيحها داخل التطبيق وتصديرها إلى الصيغ المحاسبية المدعومة.',
        'تُقدَّم الخدمة «كما هي» وهي أداة مساعدة لإدخال البيانات. وهي ليست استشارة محاسبية أو ضريبية أو قانونية، ولا تُغني عن المراجعة من قِبل شخص مؤهل.',
      ],
    },
    {
      title: 'التسجيل والحساب',
      body: [
        'يتطلب استخدام الخدمة التسجيل. وتتعهد بتقديم معلومات صحيحة وحديثة وبالحفاظ على تحديثها.',
        {
          list: [
            'أنت مسؤول عن حماية كلمة المرور والوصول إلى حسابك.',
            'أنت مسؤول عن جميع الأنشطة التي تتم عبر حسابك.',
            'تُخطرنا فورًا عند الاشتباه في وصول غير مصرّح به.',
            'لا تشارك وصولك مع أطراف خارج مؤسستك.',
          ],
        },
        'يحق لنا تعليق الحساب أو إنهاؤه في حال وقوع مخالفة جوهرية لهذه الشروط.',
      ],
    },
    {
      title: 'الاشتراك والأسعار والدفع',
      body: [
        'تُقدَّم الخدمة ضمن باقات اشتراك تتضمن عددًا محددًا من المستندات المعالَجة في كل فترة فوترة. والباقات والأسعار السارية منشورة في صفحة الأسعار.',
        {
          list: [
            'يُجدَّد الاشتراك تلقائيًا ما لم يُلغَ قبل نهاية الفترة الجارية.',
            'الأسعار باليورو ولا تشمل الضرائب المستحقة ما لم يُذكر خلاف ذلك.',
            'لا تُرحَّل المستندات غير المستخدمة إلى الفترة التالية ما لم يُتفق على ذلك صراحةً.',
            'قد يُقيَّد الوصول عند عدم السداد إلى حين تسوية المبلغ المستحق.',
          ],
        },
        'نحتفظ بحق تغيير الأسعار بإشعار مسبق لا يقل عن 30 يومًا. وتسري التغييرات اعتبارًا من فترة الفوترة التالية.',
      ],
    },
    {
      title: 'الاستخدام المقبول',
      body: [
        'عند استخدام الخدمة، تتعهد بألا:',
        {
          list: [
            'ترفع مستندات لا يحق لك معالجتها؛',
            'تستخدم الخدمة في نشاط غير قانوني، بما في ذلك معالجة مستندات مزوّرة؛',
            'تحاول الالتفاف على حدود باقتك أو على تدابير الأمان؛',
            'تُحمّل البنية التحتية عمدًا بطلبات آلية تتجاوز الحدود الموثّقة؛',
            'تستخرج الخدمة أو تنسخها أو تعيد بيعها كليًا أو جزئيًا.',
          ],
        },
      ],
    },
    {
      title: 'مستنداتك وبياناتك',
      body: [
        'تبقى المستندات التي ترفعها والبيانات المستخرجة منها ملكًا لك. ولا نكتسب أي حقوق على محتواها.',
        'نعالج المستندات لغرض تقديم الخدمة فقط — قراءتها، وتخزينها إلى حين حذفها، وتصديرها بناءً على تعليماتك. ولا نستخدم محتوى مستنداتك لتدريب النماذج، ولا نفصح عنه لأطراف ثالثة خارج المزوّدين التقنيين اللازمين لتشغيل الخدمة.',
        'معالجة البيانات الشخصية الواردة في المستندات موضّحة في <a href="/privacy-policy.html">سياسة الخصوصية</a>.',
      ],
    },
    {
      title: 'الحذف ومدة الاحتفاظ',
      body: [
        {
          highlight: [
            '<strong>لا نحتفظ بفواتيرك بعد حذفها من النظام.</strong> عند حذف مستند، يُمحى الملف وجميع البيانات المستخرجة منه — يُزال الملف من التخزين، ويُحذف السجل الذي يحمل المحتوى المستخرج نهائيًا من قاعدة البيانات.',
            'الحذف <strong>لا رجعة فيه</strong>. لا توجد سلة محذوفات للاستعادة منها، ولا نحتفظ بنسخة لأغراضنا الخاصة. وإذا كنت بحاجة إلى مستند، فنزّله أو صدّر بياناته قبل حذفه.',
          ],
        },
        'بعد الحذف نحتفظ فقط بسجل تقني للحدث نفسه — معرّف السجل، والمستخدم، والإجراء، والطابع الزمني. ولا يتضمن هذا السجل صورة المستند ولا أي بيانات مستخرجة، والغرض منه قياس الاستهلاك مقابل باقتك والحماية من إساءة الاستخدام.',
        'إلى حين حذفها، تُخزَّن المستندات لتبقى متاحة في حسابك. وعند إنهاء الحساب، تُمحى البيانات المرتبطة به.',
        'قرار حذف المستند يعود إليك وحدك. ونلفت انتباهك إلى أن التشريعات المحاسبية والضريبية قد تُلزمك بالاحتفاظ بالمستندات الأصلية لمدة محددة؛ ويبقى هذا الالتزام على عاتقك بالكامل ولا تتحمله الخدمة.',
      ],
    },
    {
      title: 'دقة الاستخراج',
      body: [
        'يتم استخراج البيانات آليًا. ورغم ما نبذله من جهد، قد تتضمن النتائج أخطاءً — خصوصًا مع رداءة جودة المسح الضوئي، أو الكتابة اليدوية، أو التنسيقات غير المألوفة، أو المستندات التالفة.',
        '<strong>أنت مسؤول عن التحقق من البيانات المستخرجة</strong> قبل الاعتماد عليها لأغراض محاسبية أو ضريبية أو غيرها. ويُنبّه التطبيق إلى بعض التعارضات، لكن هذه الفحوص مساعِدة ولا تضمن الاكتمال.',
      ],
    },
    {
      title: 'التوافر والدعم',
      body: [
        'نبذل جهودًا معقولة لإبقاء الخدمة متاحة، لكننا لا نضمن التشغيل دون انقطاع. وقد تحدث فترات صيانة مجدولة نسعى للإعلان عنها مسبقًا.',
        'تعتمد الخدمة على مزوّدي موارد حوسبة خارجيين. وقد يؤثر انقطاع لدى أحد هؤلاء المزوّدين على التوافر.',
      ],
    },
    {
      title: 'الملكية الفكرية',
      body: [
        'المنصة وشفرتها المصدرية وواجهتها ووثائقها وعلامة Invoicescan مملوكة لنا أو مرخّصة لنا حسب الأصول. وتمنحك هذه الشروط حقًا محدودًا وغير قابل للتحويل وغير حصري لاستخدام الخدمة طوال مدة اشتراكك.',
      ],
    },
    {
      title: 'حدود المسؤولية',
      body: [
        'إلى أقصى حد يسمح به القانون الواجب التطبيق، لا نتحمل المسؤولية عن الأرباح الفائتة أو فقدان البيانات أو الأضرار الناشئة عن بيانات مستخرجة بشكل غير دقيق أو الأضرار غير المباشرة الناتجة عن استخدام الخدمة أو تعذّر استخدامها.',
        'لا تتجاوز مسؤوليتنا الإجمالية تجاهك عن أي فترة اثني عشر شهرًا المبلغ الذي دفعته مقابل الخدمة خلال الفترة نفسها.',
        'لا تستبعد هذه الحدود المسؤولية التي لا يجوز استبعادها بموجب أحكام القانون الآمرة.',
      ],
    },
    {
      title: 'الإنهاء',
      body: [
        'يمكنك التوقف عن استخدام الخدمة في أي وقت بإلغاء اشتراكك أو حذف حسابك.',
        'يحق لنا إنهاء الوصول أو تقييده في حال وقوع مخالفة جوهرية لهذه الشروط، أو عدم السداد، أو سلوك يهدد أمن الخدمة وأمن سائر المستخدمين.',
        'قبل الإنهاء، صدّر ما تحتاجه من بيانات — فبعد محوها يتعذر استرجاعها.',
      ],
    },
    {
      title: 'تعديل الشروط',
      body: [
        'يجوز لنا تعديل هذه الشروط. وفي حال التغييرات الجوهرية سنُخطرك عبر البريد الإلكتروني أو داخل التطبيق قبل 30 يومًا على الأقل من سريانها. ويُعدّ استمرارك في الاستخدام بعد ذلك التاريخ قبولًا للشروط المعدّلة.',
      ],
    },
    {
      title: 'القانون الواجب التطبيق وتسوية النزاعات',
      body: [
        'يُطبَّق القانون البلغاري على ما لم يرد بشأنه نص هنا. ويسعى الطرفان إلى تسوية النزاعات وديًا، وعند تعذّر ذلك تختص المحاكم البلغارية.',
        'إذا كنت مستهلكًا، تبقى حقوقك القانونية غير متأثرة.',
      ],
    },
    {
      title: 'التواصل',
      body: [
        'للاستفسارات المتعلقة بهذه الشروط، راسلنا على <a href="mailto:invoicescan.eu@gmail.com">invoicescan.eu@gmail.com</a>.',
      ],
    },
  ],

  // ------------------------------------------------------------------ HE ----
  he: [
    {
      title: 'תחולה וקבלת התנאים',
      body: [
        'תנאים אלה מסדירים את היחסים בין Invoicescan («הספק», «אנחנו») לבין כל אדם העושה שימוש בפלטפורמת Invoicescan, ביישום הרשת ובממשקי התכנות הנלווים («השירות»).',
        'בפתיחת חשבון או בשימוש בשירות אתה מאשר כי קראת תנאים אלה וקיבלת אותם. אם אינך מסכים להם, אל תשתמש בשירות.',
        'אם אתה משתמש בשירות בשם תאגיד, אתה מצהיר כי אתה מוסמך לחייב אותו בתנאים אלה.',
      ],
    },
    {
      title: 'תיאור השירות',
      body: [
        'Invoicescan קולט מסמכים שאתה מעלה — תמונות וקובצי PDF של חשבוניות, קבלות, הודעות זיכוי וחיוב — ומחלץ מהם נתונים מובנים: צדדים נגדיים, מספרים ותאריכים, שורות פריטים, כמויות, בסיסי מס, שיעורים וסכומים.',
        'את הנתונים המחולצים ניתן לבדוק ולתקן בתוך היישום ולייצא לפורמטים החשבונאיים הנתמכים.',
        'השירות ניתן «כמות שהוא» ומהווה כלי עזר להזנת נתונים. אין הוא מהווה ייעוץ חשבונאי, מיסויי או משפטי, ואינו מחליף בדיקה בידי גורם מוסמך.',
      ],
    },
    {
      title: 'הרשמה וחשבון',
      body: [
        'השימוש בשירות מחייב הרשמה. אתה מתחייב למסור מידע נכון ועדכני ולשמור על עדכניותו.',
        {
          list: [
            'האחריות לשמירת הסיסמה והגישה לחשבונך מוטלת עליך.',
            'אתה אחראי לכל פעולה המבוצעת באמצעות חשבונך.',
            'עליך להודיע לנו מיד בכל חשד לגישה בלתי מורשית.',
            'אין למסור את גישתך לצדדים שלישיים מחוץ לארגון שלך.',
          ],
        },
        'אנו רשאים להשעות או לסגור חשבון במקרה של הפרה מהותית של תנאים אלה.',
      ],
    },
    {
      title: 'מנוי, מחירים ותשלום',
      body: [
        'השירות מוצע בתוכניות מנוי הכוללות מספר מוגדר של מסמכים מעובדים בכל תקופת חיוב. התוכניות והמחירים העדכניים מפורסמים בעמוד המחירים.',
        {
          list: [
            'המנוי מתחדש אוטומטית, אלא אם בוטל לפני תום התקופה הנוכחית.',
            'המחירים נקובים באירו ואינם כוללים מסים החלים, אלא אם צוין אחרת.',
            'מסמכים שלא נוצלו אינם מועברים לתקופה הבאה, אלא אם הוסכם במפורש.',
            'באי-תשלום ניתן להגביל את הגישה עד להסדרת החוב.',
          ],
        },
        'אנו רשאים לשנות מחירים בהודעה מוקדמת של 30 ימים לפחות. השינוי ייכנס לתוקף מתקופת החיוב הבאה.',
      ],
    },
    {
      title: 'שימוש מותר',
      body: [
        'בעת השימוש בשירות אתה מתחייב שלא:',
        {
          list: [
            'להעלות מסמכים שאינך רשאי לעבד;',
            'להשתמש בשירות לפעילות בלתי חוקית, לרבות עיבוד מסמכים מזויפים;',
            'לנסות לעקוף את מגבלות התוכנית שלך או את אמצעי האבטחה;',
            'להעמיס במכוון על התשתית בבקשות אוטומטיות מעבר למגבלות המתועדות;',
            'לגרד, להעתיק או למכור מחדש את השירות או חלק ממנו.',
          ],
        },
      ],
    },
    {
      title: 'המסמכים והנתונים שלך',
      body: [
        'המסמכים שאתה מעלה והנתונים המחולצים מהם נשארים שלך. איננו רוכשים זכויות בתוכנם.',
        'אנו מעבדים את המסמכים אך ורק לצורך אספקת השירות — קריאתם, אחסונם עד למחיקתם וייצואם לפי הוראתך. איננו משתמשים בתוכן המסמכים שלך לאימון מודלים ואיננו מוסרים אותו לצדדים שלישיים מעבר לספקים הטכניים הדרושים להפעלת השירות.',
        'עיבוד המידע האישי הכלול במסמכים מתואר ב<a href="/privacy-policy.html">מדיניות הפרטיות</a>.',
      ],
    },
    {
      title: 'מחיקה ותקופת שמירה',
      body: [
        {
          highlight: [
            '<strong>איננו שומרים את החשבוניות שלך לאחר מחיקתן מהמערכת.</strong> בעת מחיקת מסמך, הקובץ וכל הנתונים שחולצו ממנו נמחקים — הקובץ מוסר מהאחסון, והרשומה הנושאת את התוכן המחולץ נמחקת לצמיתות ממסד הנתונים.',
            'המחיקה היא <strong>בלתי הפיכה</strong>. אין סל מיחזור לשחזור, ואיננו שומרים עותק לצרכינו. אם אתה זקוק למסמך, הורד או ייצא את נתוניו לפני המחיקה.',
          ],
        },
        'לאחר המחיקה נשמרת אצלנו רק רשומה טכנית של האירוע עצמו — מזהה הרשומה, המשתמש, הפעולה וחותמת הזמן. רשומה זו אינה כוללת את תמונת המסמך ואף לא נתונים מחולצים, ותכליתה מדידת השימוש מול תוכנית המנוי שלך והגנה מפני ניצול לרעה.',
        'עד למחיקתם, המסמכים מאוחסנים כדי שיישארו זמינים בחשבונך. עם סגירת החשבון, הנתונים הקשורים אליו נמחקים.',
        'ההחלטה מתי למחוק מסמך היא שלך בלבד. שים לב כי דיני החשבונאות והמס עשויים לחייב אותך לשמור את המסמכים המקוריים לתקופה קצובה; חובה זו נותרת כולה עליך ואינה מוטלת על השירות.',
      ],
    },
    {
      title: 'דיוק החילוץ',
      body: [
        'חילוץ הנתונים נעשה באופן אוטומטי. חרף מאמצינו, התוצאות עשויות לכלול שגיאות — במיוחד בסריקה באיכות ירודה, בכתב יד, בפריסות חריגות או במסמכים פגומים.',
        '<strong>האחריות לבדיקת הנתונים המחולצים מוטלת עליך</strong> בטרם תסתמך עליהם לצרכים חשבונאיים, מיסוייים או אחרים. היישום מסמן חלק מאי-ההתאמות, אך בדיקות אלה הן כלי עזר ואינן מבטיחות שלמות.',
      ],
    },
    {
      title: 'זמינות ותמיכה',
      body: [
        'אנו עושים מאמצים סבירים לשמור על זמינות השירות, אך איננו מתחייבים לפעילות רציפה. ייתכנו חלונות תחזוקה מתוכננים, ואנו שואפים להודיע עליהם מראש.',
        'השירות תלוי בספקי משאבי מחשוב חיצוניים. תקלה אצל ספק כזה עלולה להשפיע על הזמינות.',
      ],
    },
    {
      title: 'קניין רוחני',
      body: [
        'הפלטפורמה, קוד המקור, הממשק, התיעוד והמותג Invoicescan הם בבעלותנו או מורשים לנו כדין. תנאים אלה מעניקים לך זכות מוגבלת, בלתי ניתנת להעברה ובלתי בלעדית להשתמש בשירות למשך תקופת המנוי.',
      ],
    },
    {
      title: 'הגבלת אחריות',
      body: [
        'במידה המרבית המותרת בדין החל, איננו אחראים לאובדן רווחים, לאובדן נתונים, לנזקים הנובעים מנתונים שחולצו באופן שגוי או לנזקים עקיפים הנובעים מהשימוש בשירות או מאי-היכולת להשתמש בו.',
        'אחריותנו הכוללת כלפיך בכל תקופה של שנים עשר חודשים לא תעלה על הסכום ששילמת עבור השירות באותה תקופה.',
        'הגבלות אלה אינן שוללות אחריות שלא ניתן לשלול לפי הוראות דין קוגנטיות.',
      ],
    },
    {
      title: 'סיום ההתקשרות',
      body: [
        'באפשרותך להפסיק את השימוש בשירות בכל עת, בביטול המנוי או במחיקת החשבון.',
        'אנו רשאים לסיים או להגביל את הגישה במקרה של הפרה מהותית של תנאים אלה, באי-תשלום, או בהתנהלות המסכנת את אבטחת השירות ואת יתר המשתמשים.',
        'לפני הסיום ייצא את הנתונים הדרושים לך — לאחר מחיקתם לא ניתן לשחזרם.',
      ],
    },
    {
      title: 'שינויים בתנאים',
      body: [
        'אנו רשאים לתקן תנאים אלה. בשינויים מהותיים נודיע לך בדוא"ל או בתוך היישום לפחות 30 ימים לפני כניסתם לתוקף. המשך השימוש לאחר מועד זה מהווה הסכמה לתנאים המתוקנים.',
      ],
    },
    {
      title: 'הדין החל ויישוב מחלוקות',
      body: [
        'על עניינים שלא הוסדרו כאן יחול הדין הבולגרי. הצדדים ישאפו ליישב מחלוקות בהסכמה, ובהיעדר הסכמה — בבית המשפט הבולגרי המוסמך.',
        'אם אתה צרכן, זכויותיך על פי דין נותרות בעינן.',
      ],
    },
    {
      title: 'יצירת קשר',
      body: [
        'שאלות בנוגע לתנאים אלה ניתן לשלוח אל <a href="mailto:invoicescan.eu@gmail.com">invoicescan.eu@gmail.com</a>.',
      ],
    },
  ],
};
